import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useState, type ReactNode } from 'react'
import { z } from 'zod'
import { createClient } from '@kairos/supabase/browser'

import {
  ErrorBanner,
  Field,
  Stack,
  StatusCard,
  SubmitButton,
} from '~/components/auth/auth-ui'
import { logger } from '~/lib/logger'

/**
 * `/authentication/verify-email` — recovery sub-surface 6 from
 * `02-authentication.md`.
 *
 * Variants:
 *  - `form`    — email + 6-digit OTP entry. Default.
 *  - `expired` — destructive card "Code expired" with link to retry.
 *
 * The email is supplied via `?email=…` in the URL (set when we redirect the
 * user here from signup / sign-in). If absent, we render the form with an
 * editable email field so the OTP can still be verified.
 *
 * Calls `supabase.auth.verifyOtp({ email, token, type: 'email' })`. On
 * success we route to `/admin` — the parent admin layout will perform the
 * super-admin gate check and bounce non-admins to /dashboard.
 */

const searchSchema = z.object({
  state: z.enum(['form', 'expired']).optional(),
  email: z.string().email().optional(),
})

const formSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  token: z
    .string()
    .min(6, 'Enter the 6-digit code')
    .max(6, 'Enter the 6-digit code')
    .regex(/^\d{6}$/, 'Codes are 6 digits'),
})

type FormValues = z.infer<typeof formSchema>

export const Route = createFileRoute('/authentication/verify-email')({
  validateSearch: searchSchema,
  component: VerifyEmailPage,
})

function VerifyEmailPage(): ReactNode {
  const navigate = useNavigate()
  const search = Route.useSearch()
  const [variant, setVariant] = useState<'form' | 'expired'>(search.state ?? 'form')
  const [bannerError, setBannerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: search.email ?? '',
      token: '',
    },
  })

  async function onSubmit(values: FormValues): Promise<void> {
    setBannerError(null)
    try {
      const supabase = createClient()
      const { error } = await supabase.auth.verifyOtp({
        email: values.email,
        token: values.token,
        type: 'email',
      })
      if (error) {
        // Supabase returns 401 / 403 for invalid OR expired codes — surface
        // an inline "wrong code" banner. Specifically expired codes flip to
        // the destructive variant.
        const message = (error.message || '').toLowerCase()
        if (message.includes('expired')) {
          setVariant('expired')
          return
        }
        if (error.status === 429) {
          setBannerError('Too many attempts. Try again in a few minutes.')
          return
        }
        setBannerError('That code didn&rsquo;t match. Try again.')
        return
      }
      void navigate({ to: '/admin' })
    } catch (cause: unknown) {
      logger.warn('verify-email unexpected error', {
        message: cause instanceof Error ? cause.message : 'unknown',
      })
      setBannerError('Something went wrong. Try again.')
    }
  }

  if (variant === 'expired') {
    return (
      <StatusCard
        tone="destructive"
        glyph="!"
        title="Code expired"
        body="Verification codes are valid for 10 minutes. Request a new one and try again."
      >
        <button
          type="button"
          onClick={() => setVariant('form')}
          className="ka-btn ka-btn--primary ka-auth-cta-mt"
        >
          Enter a new code
        </button>
      </StatusCard>
    )
  }

  return (
    <Stack>
      <header>
        <h1 className="ka-auth-h1">Verify your email</h1>
        <p className="ka-auth-sub">
          {search.email ? (
            <>
              We sent a 6-digit code to <strong>{search.email}</strong>.
            </>
          ) : (
            'Enter the 6-digit code we emailed you.'
          )}
        </p>
      </header>

      {bannerError ? <ErrorBanner message={bannerError} /> : null}

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="ka-stack">
        {!search.email ? (
          <Field
            id="email"
            label="Email"
            type="email"
            placeholder="you@kairos.com"
            autoComplete="email"
            error={errors.email?.message}
            {...register('email')}
          />
        ) : (
          <input type="hidden" {...register('email')} />
        )}

        <Field
          id="token"
          label="6-digit code"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder="000000"
          maxLength={6}
          error={errors.token?.message}
          className="ka-field-input--otp"
          {...register('token')}
        />

        <SubmitButton loading={isSubmitting} label="Verify" loadingLabel="Verifying…" />
      </form>

      <p className="ka-field-helper ka-auth-text-center">
        Wrong email?{' '}
        <Link to="/authentication/login" className="ka-link">
          Sign out and try again
        </Link>
      </p>
    </Stack>
  )
}
